import { Box, Button, Grid, List, ListItemText, Stack, Typography } from '@mui/material';
import { ScrollMode } from '@react-pdf-viewer/core';
import jsPDF from 'jspdf';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';

import { LosType } from '@modules/ContactManager/components/constants';
import { formatDate, queryParams } from '@shared/commonFunctions';
import { Modal } from '@shared/components/modals/Modal';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { PdfViewer } from '@shared/components/PdfViewer';
import { SERVICES } from '@shared/constants/apiConfig';
import { reportDateFormat } from '@shared/constants/formats';
import { IStipType } from '@shared/interfaces';
import { setRefreshFiles } from '@store/reducers/contactManagerSlice';
import { ListHeader, ListRow, StyledListLabel, StyledListValue } from '@styles/ContactManager/FinancingDetailsModalStyles';
import { VehicleFeaturesLoaderWrapper } from '@styles/Shared/StyledLoaders';

import { IStipFile } from '../../../../ContactFiles/interfaces';
import { requestUploadStipFiles } from '../../../../ContactFiles/requests';
import { FinancingDetailsModalProps, IFinanceDetails, ListDecision, ListItemField } from '../../../interfaces';
import { LenderResponseDisplayTypes } from '../interfaces';
import { OL_CODES } from '../../constants';
import { ViewXmlModal } from './ViewXmlModal';

const DefaultText = ({ text }: { text?: string }) => {
  const defaultText = text ?? 'No financing details to display';

  return (
    <VehicleFeaturesLoaderWrapper style={{ height: '300px' }}>
      <Typography
        display="block"
        textAlign="center"
      >
        {defaultText}
      </Typography>
    </VehicleFeaturesLoaderWrapper>
  );
};

// As at the backend its an index based enum unless the index chanages at be this will work
// const Status = ['Undefined', 'Approved', 'Countered', 'Declined', 'Certified', 'Failed', 'Error', 'Eligible'];

export const FinancingDetailsModal = ({
  deal,
  onClose,
  dealId = '',
  assetId,
  financingModalPosition,
}: FinancingDetailsModalProps) => {
  const params = queryParams();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [showXml, setShowXml] = useState(0);
  const [html, setHTML] = useState<{ __html: string }>({ __html: '' });

  const { data: financingDetails, error } = useSWR<IFinanceDetails>(
    dealId && `${SERVICES.DEAL}/financing-details/deals/${dealId}`
  );
  const { data: stipType } = useSWR<IStipType[]>(`${SERVICES.DOCUMENT}/stipTypes?name=Approval`);
  const ref = useRef<HTMLDivElement>(null);
  const isCudl = deal?.dealLos?.losType === LosType.Cudl;
  const isOpenLending = deal?.lenderResponse.lenderCode
    ? Object.values(OL_CODES).includes(deal?.lenderResponse.lenderCode)
    : false;
  const showHeaderBtn = isOpenLending ? true : !isCudl;

  const stipTypeId: string = useMemo(() => stipType?.filter((i) => i.name === 'Approval')[0].id || '', [stipType]);

  const renderHTML = useMemo(
    () => financingDetails?.lenderResponseDisplayType === LenderResponseDisplayTypes.Html,
    [financingDetails]
  );

  useEffect(() => {
    const renderContent = async () => {
      const resp = `${financingDetails?.contentDecision}`;
      return { __html: resp };
    };
    renderContent().then((result) => setHTML(result));
  }, [financingDetails]);

  useEffect(() => {
    if (error) {
      Notistack.generalError(error?.response?.data?.message);
      onClose();
    }
  }, [error]);

  const renderItems = (items: ListItemField[], ref?: React.RefObject<HTMLDivElement>) => (
    <Box
      maxHeight="460px"
      overflow="auto"
    >
      <Box ref={ref}>
        {items?.map((item: ListItemField) => (
          <ListRow
            key={item.name}
            divider={true}
          >
            <StyledListLabel>{item.name}</StyledListLabel>
            <StyledListValue>{item?.values?.join(',')}</StyledListValue>
          </ListRow>
        ))}
      </Box>
    </Box>
  );

  const showData = useMemo(
    () => financingDetails?.contentDecision || financingDetails?.contentApplication,
    [financingDetails?.contentDecision, financingDetails?.contentApplication]
  );

  const handleDownload = useCallback(async () => {
    const canDownload = renderHTML ? !!financingDetails?.contentDecision : !!financingDetails?.decisionDisplayItems;

    if (!canDownload) {
      Notistack.generalError('Unable to generate approval response on this deal.');
      return;
    }

    const data = ref.current;

    if (data) {
      setLoading(true);
      const pdf = new jsPDF('p', 'mm');
      const pageHeight = pdf.internal.pageSize.height;
      let cursorY = 7; // Initial Y position for text

      if (renderHTML) {
        pdf.setFontSize(8);
        await pdf.html(data, {
          html2canvas: {
            scale: 0.1,
          },
        });
      } else {
        const yIncrementor = 5;

        financingDetails?.decisionDisplayItems.forEach((item) => {
          //Split the text into multiple lines, ensuring each line does not exceed 150 units in width
          const text = pdf.splitTextToSize(item.values.join(','), 150);

          // Calculate the total line height for the split text
          const lineHeight = text.length * yIncrementor;

          // Add a new page if the next text block exceeds the page height
          if (cursorY + lineHeight > pageHeight - 10) {
            pdf.addPage();
            cursorY = 7; // Reset Y position for the new page
          }

          pdf.setFontSize(8);
          pdf.setFont('', '', 'bold');
          pdf.text(item.name, 5, cursorY);

          pdf.setFontSize(8);
          pdf.setFont('', '', 'normal');
          pdf.text(text, 40, cursorY);
          cursorY += lineHeight;
        });
      }

      const fileName = `ApprovalResponse_${deal?.numericId ?? 0}_${formatDate(new Date().toISOString(), {
        format: reportDateFormat,
      })}.pdf`;
      pdf.save(fileName);
      const blobData = pdf.output('blob');
      const file = new File([blobData], fileName);
      const postData: IStipFile = {
        file: file,
        assetId: assetId,
        contactId: params.id,
        dealId: dealId,
        borrowerCoBorrowerType: '',
        stipTypeId: stipTypeId || '', // for Approval
      };
      await requestUploadStipFiles(postData);
      dispatch(setRefreshFiles(new Date().toISOString()));
      setLoading(false);
    }
  }, [ref.current, deal?.numericId, params.id, dealId, assetId, stipTypeId, financingDetails?.decisionDisplayItems, renderHTML]);

  return (
    <Modal
      open
      hideBackdrop
      resetPosition={financingModalPosition}
      isLoading={Boolean(loading || (!financingDetails && !error))}
      title="Financing Details"
      dialogWidth="1000px"
      dialogMinHeight="300px"
      headerButtonText="Download"
      showHeaderBtn={showHeaderBtn}
      onHeaderBtnClick={handleDownload}
      onClose={onClose}
    >
      {renderHTML ? (
        !financingDetails?.contentDecision ? (
          <DefaultText />
        ) : (
          <Box
            maxHeight="700px"
            overflow="auto"
          >
            <Box
              ref={ref}
              dangerouslySetInnerHTML={html}
            />
          </Box>
        )
      ) : showData &&
        (!isCudl ||
          (isCudl && (financingDetails?.applicationDisplayItems?.length || financingDetails?.decisionDisplayItems?.length))) ? (
        <Box>
          <Stack>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                md={5.5}
              >
                <List>
                  <ListHeader divider={true}>
                    <ListItemText sx={{ width: '50%' }}>Application</ListItemText>
                    <ListItemText></ListItemText>
                  </ListHeader>
                  {renderItems(financingDetails?.applicationDisplayItems ?? [])}
                </List>
              </Grid>
              <Grid
                item
                md={6.5}
              >
                <List style={{ maxHeight: '480px' }}>
                  <ListHeader divider={true}>
                    <ListItemText sx={{ width: '50%' }}>Decision</ListItemText>
                    <ListItemText></ListItemText>
                  </ListHeader>
                  {financingDetails?.decisionDisplayItems && renderItems(financingDetails.decisionDisplayItems ?? [], ref)}
                </List>
                {!financingDetails?.decisionDisplayItems && <DefaultText text="Response Pending" />}
                {financingDetails?.decisionStatuses?.length > 0 && (
                  <div>
                    <List>
                      <ListHeader divider={true}>
                        <ListItemText sx={{ width: '50%' }}>Status</ListItemText>
                        <ListItemText></ListItemText>
                      </ListHeader>
                      {/* Add your items for the new list here */}
                      <Box
                        maxHeight="610px"
                        overflow="auto"
                      >
                        <Box>
                          {(financingDetails?.decisionStatuses ?? [])?.map((item: ListDecision) => (
                            <ListRow
                              key={item.status}
                              divider={true}
                            >
                              <StyledListLabel>{item.status}</StyledListLabel>
                              <StyledListValue>{item.statusDate}</StyledListValue>
                            </ListRow>
                          ))}
                        </Box>
                      </Box>
                    </List>
                  </div>
                )}
              </Grid>
            </Grid>
          </Stack>
          <Button
            size="small"
            variant="contained"
            onClick={() => setShowXml((prevState) => prevState + 1)}
          >
            View XML
          </Button>
        </Box>
      ) : isCudl ? (
        <Box>
          <PdfViewer
            scrollMode={ScrollMode.Vertical}
            fileSource={financingDetails?.contentDecision}
          />
        </Box>
      ) : (
        <DefaultText />
      )}
      {!!showXml && (
        <ViewXmlModal
          viewXmlModalPosition={showXml}
          data={financingDetails}
          onClose={() => setShowXml(0)}
        />
      )}
    </Modal>
  );
};
